import { useMemo } from "react";
import { ENDPOINTS } from "@/constants/api/endpoints";
import { useApiQuery } from "../../hooks/use-api-query";
import { queryKeys } from "../../query-keys";
import type { CityAutocompleteResp, CitySuggestion } from "@/types/city";

/** Minimum characters before hitting the typeahead endpoint. */
export const CITY_AUTOCOMPLETE_MIN_LENGTH = 3;

/**
 * Server-side city typeahead (`GET /search/cities/autocomplete?q=`).
 *
 * Callers should debounce the input and only pass a trimmed query. Requests
 * are gated at {@link CITY_AUTOCOMPLETE_MIN_LENGTH} chars; React Query aborts
 * the previous in-flight fetch when the query key changes.
 */
export const useCityAutocomplete = (search: string) => {
  const query = search.trim();
  const enabled = query.length >= CITY_AUTOCOMPLETE_MIN_LENGTH;

  const result = useApiQuery<CityAutocompleteResp>(
    queryKeys.search.cityAutocomplete(query),
    `${ENDPOINTS.ACCOMMODATIONS.CITY_AUTOCOMPLETE}?q=${encodeURIComponent(query)}`,
    { showErrorToast: false },
    {
      enabled,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: false,
      refetchOnWindowFocus: false,
      placeholderData: (previous) => previous,
    },
  );

  const suggestions = useMemo<CitySuggestion[]>(() => {
    const items = result.data?.data;
    if (!Array.isArray(items)) return [];

    return items.filter((item) => !!item?.name && !!item?.slug);
  }, [result.data]);

  return { ...result, suggestions, enabled };
};
